import { ReactNode, ElementType, ComponentProps } from "react";
import { cn, tokens } from "@blurb/codex-react";
import { useTextStyles } from "../theme/typographyStyles";

export interface TextComponentProps<T extends keyof JSX.IntrinsicElements> {
  children: ReactNode;
  size?: keyof typeof tokens.font.size;
  weight?: keyof typeof tokens.font.weight;
  as?: T;
  className?: string;
}

/**
 * Body text component - Standard text for content
 */
export function Body<T extends keyof JSX.IntrinsicElements = "div">({
  children,
  size,
  weight,
  as,
  className,
  ...componentProps
}: TextComponentProps<T> & ComponentProps<T>) {
  const textClasses = useTextStyles();
  const Component = (as ?? "div") as ElementType;
  return (
    <Component className={cn(textClasses("body", size, weight), className)} {...componentProps}>
      {children}
    </Component>
  );
}

/**
 * Heading component - For headings and titles
 */
export function Heading<T extends keyof JSX.IntrinsicElements = "div">({
  children,
  size,
  weight,
  as,
  className,
  ...componentProps
}: TextComponentProps<T> & ComponentProps<T>) {
  const textClasses = useTextStyles();
  const Component = (as ?? "div") as ElementType;
  return (
    <Component className={cn(textClasses("heading", size, weight), className)} {...componentProps}>
      {children}
    </Component>
  );
}

/**
 * Label component - For form labels and emphasis
 *
 * Renders as a `div` by default. For accessible form labels, use `as="label"` with the `htmlFor` prop or nest the input inside the label
 * @example
 * <Label as="label" htmlFor="email">Email Address</Label>
 * <input id="email" />
 * @example
 * <Label as="label">
 *  Email Address
 *  <input />
 * </Label>
 */
export function Label<T extends keyof JSX.IntrinsicElements = "div">({
  children,
  size,
  weight,
  as,
  className,
  ...componentProps
}: TextComponentProps<T> & ComponentProps<T>) {
  const textClasses = useTextStyles();
  const Component = (as ?? "div") as ElementType;
  return (
    <Component className={cn(textClasses("label", size, weight), className)} {...componentProps}>
      {children}
    </Component>
  );
}
